import { useEffect, useRef, useState } from "react";
import EMSProject from "./EMSProject";
import CCTVProject from "./CCTVProject";
import PGLProject from "./PGLProject";
import pglCover from "./assets/pgl/after-gaming-stations.jpg";
import emsLogo from "./assets/ems/logo.png";
import cctvCover from "./assets/cctv/monitoring-station.jpg";

const projects = [
  {
    id: "ems",
    category: "Software",
    title: "Employee Management System (EMS)",
    summary:
      "An all-in-one HR and payroll platform that automates attendance, timekeeping, employee requests, and payroll processing—helping businesses reduce manual work and manage their workforce more efficiently.",
    cover: emsLogo,
    coverAlt: "Employee Management System logo",
    coverClass: "bg-black object-contain p-8",
    Details: EMSProject,
  },
  {
    id: "apartment-cctv",
    category: "Hardware",
    title: "Apartment CCTV Installation",
    summary:
      "Eight cameras installed at an apartment building, with cable routing and a central monitoring station.",
    cover: cctvCover,
    coverAlt: "Apartment CCTV equipment cabinet and multi-camera display",
    coverClass: "bg-slate-100 object-cover object-center",
    Details: CCTVProject,
  },
  {
    id: "pgl-computer-cleaning",
    category: "Hardware",
    title: "PGL Computer Cleaning & Maintenance",
    summary:
      "Detailed cleaning of 22 gaming PCs at Protocol Gaming Lounge, with Noctua NT-H2 thermal paste replacement.",
    cover: pglCover,
    coverAlt: "Gaming computers at Protocol Gaming Lounge after cleaning",
    coverClass: "bg-slate-950 object-cover object-center",
    Details: PGLProject,
  },
];

function ProjectModal({ project, opener, onClose }) {
  const dialogRef = useRef(null);
  const contactRequested = useRef(false);
  const { Details } = project;

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    if (!dialog.open) dialog.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [opener]);

  const close = () => dialogRef.current.close();
  const handleContact = (event) => {
    event.preventDefault();
    contactRequested.current = true;
    close();
  };
  const handleClose = () => {
    onClose();
    if (contactRequested.current) {
      // Wait for the modal cleanup to restore scrolling and focus before navigating.
      window.requestAnimationFrame(() => {
        const contact = document.getElementById("contact");
        if (!contact) return;
        window.location.hash = "contact";
        contact.scrollIntoView({ block: "center" });
        contact.querySelector("input")?.focus({ preventScroll: true });
      });
    }
  };
  const handleBackdropClick = (event) => {
    if (event.target !== event.currentTarget) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    if (
      event.clientX < bounds.left ||
      event.clientX > bounds.right ||
      event.clientY < bounds.top ||
      event.clientY > bounds.bottom
    )
      close();
  };

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="project-modal-title"
      onClose={handleClose}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onClick={handleBackdropClick}
      className="project-modal fixed inset-0 m-auto max-h-[85dvh] w-[calc(100%-2rem)] max-w-5xl overflow-hidden rounded-3xl border border-white/15 bg-[#0b1b2a] p-0 text-slate-50 shadow-2xl"
    >
      <div className="project-modal__header flex shrink-0 items-center justify-between gap-4 border-b border-white/10 px-6 py-4 sm:px-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a8cdef]">
            {project.category} project
          </p>
          <h3 id="project-modal-title" className="mt-1 text-lg font-semibold">
            {project.title}
          </h3>
        </div>
        <button
          type="button"
          onClick={close}
          autoFocus
          aria-label="Close project details"
          className="shrink-0 rounded-full border border-white/15 px-4 py-2 text-sm transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-300"
        >
          Close <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div className="project-modal__body">
        <Details onContact={handleContact} />
      </div>
    </dialog>
  );
}

export default function Projects() {
  const [category, setCategory] = useState("Software");
  const [selectedProject, setSelectedProject] = useState(null);
  const openerRef = useRef(null);

  return (
    <section
      id="projects"
      className="mt-16 scroll-mt-24 border-t border-white/5 pt-12"
      aria-labelledby="projects-title"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#a8cdef]">
        Our projects
      </p>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-5">
        <div>
          <h2
            id="projects-title"
            className="text-2xl font-semibold text-slate-50"
          >
            Software & hardware solutions
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-slate-300">
            Explore our work. Select a project to see its overview and gallery.
          </p>
        </div>
        <div className="flex gap-2" role="group" aria-label="Project category">
          {["Software", "Hardware"].map((item) => (
            <button
              key={item}
              type="button"
              aria-pressed={category === item}
              onClick={() => setCategory(item)}
              className={`rounded-full border px-5 py-2 text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sky-300 ${category === item ? "border-[#2E7EAE] bg-[#2E7EAE] text-white" : "border-white/15 bg-slate-900/70 text-slate-300 hover:border-sky-300"}`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {projects
          .filter((project) => project.category === category)
          .map((project) => (
            <button
              key={project.id}
              type="button"
              onClick={(event) => {
                openerRef.current = event.currentTarget;
                setSelectedProject(project);
              }}
              aria-haspopup="dialog"
              aria-label={`View details: ${project.title}`}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-slate-900/70 text-left transition hover:-translate-y-1 hover:border-sky-300/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sky-300 motion-reduce:transform-none"
            >
              <img
                src={project.cover}
                alt={project.coverAlt}
                loading="lazy"
                className={`h-48 w-full ${project.coverClass}`}
              />
              <span className="flex flex-1 flex-col p-5">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a8cdef]">
                  {project.category}
                </span>
                <span className="mt-2 text-lg font-semibold text-slate-50">
                  {project.title}
                </span>
                <span className="mt-2 text-sm leading-relaxed text-slate-300">
                  {project.summary}
                </span>
                <span className="mt-5 text-sm font-semibold text-sky-300 group-hover:underline">
                  View project <span aria-hidden="true">&rarr;</span>
                </span>
              </span>
            </button>
          ))}
      </div>
      {selectedProject && (
        <ProjectModal
          project={selectedProject}
          opener={openerRef.current}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </section>
  );
}
